"use client"
import React, { MouseEvent, useState } from 'react';
import Image from 'next/image';
import { Box } from '@mui/material';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { Car } from '../models/Car';
import { ENV } from '@/app/environment/environment';

// Constants
const MAGNIFIER_SIZE = 200;
const ZOOM_LEVEL = 2.5;

const ImageContainer: React.FC<{ car: Car }> = ({ car }) => {
    const [zoomable, setZoomable] = useState(false);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [position, setPosition] = useState({ x: 0, y: 0, mouseX: 0, mouseY: 0 });
    const [currentIndex, setCurrentIndex] = useState(0);
    const [playingVideoIndex, setPlayingVideoIndex] = useState<number | null>(null);

    const handleMouseEnter = (e: MouseEvent) => {
        const element = e.currentTarget;
        const { width, height } = element.getBoundingClientRect();
        setImageSize({ width, height });
        setZoomable(true);
    };

    const handleMouseLeave = () => {
        setZoomable(false);
    };

    const handleMouseMove = (e: MouseEvent) => {
        updatePosition(e);
    };

    const updatePosition = (e: MouseEvent) => {
        const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - left;
        const y = e.clientY - top;

        if (x >= 0 && x <= width && y >= 0 && y <= height) {
            setPosition({
                x: -x * ZOOM_LEVEL + MAGNIFIER_SIZE / 2,
                y: -y * ZOOM_LEVEL + MAGNIFIER_SIZE / 2,
                mouseX: x - MAGNIFIER_SIZE / 2,
                mouseY: y - MAGNIFIER_SIZE / 2,
            });
        } else {
            setZoomable(false);
        }
    };

    const handleVideoClick = (index: number) => {
        setPlayingVideoIndex(index);
        setZoomable(false); // disable magnifier on video
    };

    const handleImageClick = () => {
        setPlayingVideoIndex(null); // reset back to image
    };



    return (
        <Box>
            <div className="flex justify-center items-center">
                <div
                    onMouseLeave={handleMouseLeave}
                    onMouseEnter={handleMouseEnter}
                    onMouseMove={handleMouseMove}
                    className="w-full relative overflow-hidden"
                >


                    

                    {playingVideoIndex !== null ? (
                        <video
                            src={car.videos![playingVideoIndex]}
                            controls
                            autoPlay
                            className="w-full h-auto z-10"
                        />
                    ) : (
                        <Image
                            className="border z-10"
                            alt=""
                            src={`${ENV.bucket}/images/`+car.images![currentIndex]}
                            layout="intrinsic"
                            width={1600} // Optional: Use your image native width
                            height={900} // Optional: Use your image native height
                            onClick={handleImageClick}
                        />
                    )}

                    {zoomable && playingVideoIndex === null && (
                        <div
                            style={{
                                backgroundPosition: `${position.x}px ${position.y}px`,
                                backgroundImage: `url(${ENV.bucket}/images/${car.images![currentIndex]})`,
                                backgroundSize: `${imageSize.width * ZOOM_LEVEL}px ${imageSize.height * ZOOM_LEVEL}px`,
                                backgroundRepeat: 'no-repeat',
                                top: `${position.mouseY}px`,
                                left: `${position.mouseX}px`,
                                width: `${MAGNIFIER_SIZE}px`,
                                height: `${MAGNIFIER_SIZE}px`,
                            }}
                            className="z-50 border-4 rounded-full pointer-events-none absolute border-gray-500"
                        />
                    )}
                </div>
            </div>


            {/* Thumbnails */}
            <div className="gap-2 flex items-center space-x-2 mt-6">
                {car.images!.map((img, index) => (
                    <div
                        key={`img-${index}`}
                        className="h-20 cursor-pointer w-20 transition-shadow duration-300 shadow-md hover:shadow-lg"
                        style={{
                            backgroundImage: `url(${ENV.bucket}/images/${img})`,
                            backgroundSize: 'cover',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                        }}
                        onMouseOver={() => {
                            setCurrentIndex(index);
                            setPlayingVideoIndex(null);
                        }}
                    ></div>
                ))}

                {car.videos &&
                    car.videos.map((video, index) => (
                        <div
                            key={`vid-${index}`}
                            className="relative h-20 cursor-pointer w-20 transition-shadow duration-300 shadow-md hover:shadow-lg bg-black"
                            onClick={() => handleVideoClick(index)}
                        >
                            <video
                                src={video}
                                className="object-cover w-full h-full opacity-70"
                                muted
                                preload="metadata"
                            />
                            <PlayCircleIcon
                                className="absolute text-white opacity-90 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                                fontSize="large"
                            />
                        </div>
                    ))}
            </div>

        </Box>
    );
};

export default ImageContainer;
